import React from 'react';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import Container from '../common/Container';
import SectionHeading from '../common/SectionHeading';
import Card from '../common/Card';
import Button from '../common/Button';
import { blogPosts } from '../../data/blogPosts';
import DynamicBlogImage from './DynamicBlogImage'; // Import the new component

const Blog = () => {
  // Display only the 3 most recent blog posts
  const recentPosts = blogPosts.slice(0, 3);

  // Function to determine template style based on category
  const getTemplateStyle = (category) => {
    const categoryTemplates = {
      'Automotive': 'automotive',
      'Smart Manufacturing': 'tech',
      'Industrial Automation': 'gradient',
      'Technology': 'modern'
    };
    return categoryTemplates[category] || 'gradient';
  };

  return (
    <section id="blog" className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden" aria-labelledby="blog-heading">
      {/* Static background elements */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-cyan-50 rounded-full opacity-30 blur-3xl -z-10"></div>
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-blue-50 rounded-full opacity-30 blur-3xl -z-10"></div>
      
      <Container>
        <SectionHeading
          eyebrow="Our Insights"
          title={<>Latest From Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Blog</span></>}
          description="Industry insights, technical guides, and automation trends for modern manufacturing"
          centered
          id="blog-heading"
        />
        
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {recentPosts.map((post) => (
            <Card 
              key={post.id}
              variant="default"
              className="bg-white rounded-xl shadow-sm overflow-hidden"
              padding="none"
            >
              <div className="relative overflow-hidden">
                {/* 🎯 REPLACED: Static image with dynamic template */}
                <DynamicBlogImage
                  post={post}
                  templateStyle={getTemplateStyle(post.category)}
                  className="w-full h-52"
                />
                
                {/* Keep the featured badge logic */}
                {post.featured && (
                  <div className="absolute top-4 right-4">
                    <span className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-medium rounded-full backdrop-blur-sm shadow-sm">
                      Featured
                    </span>
                  </div>
                )}
              </div>
              
              <div className="p-6 pb-8">
                <div className="flex flex-wrap items-center text-xs text-slate-500 mb-4 gap-4">
                  <span className="flex items-center">
                    <Calendar className="w-3.5 h-3.5 mr-1 text-cyan-600" aria-hidden="true" />
                    {post.date}
                  </span>
                  <span className="flex items-center">
                    <Clock className="w-3.5 h-3.5 mr-1 text-cyan-600" aria-hidden="true" />
                    {post.readTime}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold mb-3 text-slate-900 line-clamp-2">
                  <Link 
                    to={`/blog/${post.id}`}
                    className="block"
                  >
                    {post.title}
                  </Link>
                </h3>
                
                <p className="text-slate-600 mb-6 line-clamp-3">
                  {post.excerpt}
                </p>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="flex justify-center mt-12">
          <Button 
            variant="primary" 
            to="/blog" 
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg"
          >
            View All Articles
          </Button>
        </div>
      </Container>
    </section>
  );
};

export default Blog;